import { Link } from "react-router-dom";
import { FaArrowLeft, FaEnvelope, FaShieldAlt } from "react-icons/fa";

const sections = [
  {
    title: "1. Introduction",
    content: (
      <div className="space-y-4">
        <p>
          TripReserver (TripReserver,” “we,” “us,” or “our”) and its related
          companies value your trust and want you to be familiar with how we
          collect, use, and disclose information. This Privacy Policy describes
          our practices in connection with information that we collect when you
          visit or otherwise use any of our websites (including, but not limited
          to, www.TripReserver.com) (collectively, the “Websites”), software
          applications made available by us for use on mobile devices (the
          “Apps”), or services provided through our various channels, including,
          but not limited to, our Websites, Apps, and contact centers (the
          “Services”).
        </p>
        <p>
          By visiting or otherwise using any of our Websites, Apps, or Services,
          you are agreeing to the terms and conditions of this Privacy Policy
          and the accompanying Terms and Conditions.
        </p>
      </div>
    ),
  },
  {
    title: "2. Personal Information We May Collect",
    content: (
      <div className="space-y-4">
        <p>
          "Personal Information" is information that identifies you as an
          individual or relates to an identifiable person, including, for
          example:
        </p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>Name</li>
          <li>Postal addresses</li>
          <li>Telephone number</li>
          <li>Email address</li>
          <li>Credit and debit card number and related information</li>
          <li>Social media account ID</li>
          <li>Passport number</li>
          <li>
            Number assigned by a transportation agency, such as a Transportation
            Security Administration known traveler number
          </li>
          <li>
            Any other information you provide when you communicate or book
            travel with us (e.g., your dietary and other preferences or needs),
            as well as information about your bookings (e.g., points of
            departure and destinations)
          </li>
          <li>Demographic information</li>
          <li>
            Analytics/Advertising Data (such as where tied to a user/device ID)
          </li>
          <li>Inferences about you</li>
        </ul>
        <p>
          If you submit any Personal Information relating to other people to us
          or to our service providers in connection with the Services, you
          represent that you have the authority to do so and to permit us to use
          the information in accordance with this Privacy Policy.
        </p>
      </div>
    ),
  },
  {
    title: "3. How We May Collect Information",
    content: (
      <div className="space-y-4">
        <p className="font-semibold text-slate-800">
          How We May Collect Personal Information
        </p>
        <p>
          We and other third parties may collect Personal Information in a
          variety of ways, including:
        </p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>
            <strong>Through the Services:</strong> We may collect Personal
            Information through the Services (e.g., when you provide it to us or
            where such information is collected automatically), such as when you
            make a booking, register for a promotion, or provide a preferred
            means of communication or special travel requests.
          </li>
          <li>
            <strong>Offline:</strong> We may collect Personal Information from
            you offline, such as when you contact customer service.
          </li>
          <li>
            <strong>From Other Sources:</strong> We may receive your Personal
            Information from other sources, such as public databases, joint
            marketing partners, social media platforms, and other third parties,
            to the extent permitted by applicable law.
          </li>
        </ul>
        <p className="font-semibold text-slate-800 mt-4">
          Analytics/Advertising Data
        </p>
        <p>
          As is true of many digital properties, we and our third-party partners
          may automatically collect or process information you provide to us and
          information about your device and visit/use of the Services. The type
          of information may include the following:
        </p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>
            Browser and device information (e.g., IP address, device or other
            digital user IDs, browser or device type and version).
          </li>
          <li>
            Services usage data (path taken to, through, or when exiting our
            Services, what page you are on or have visited, links clicked,
            videos or other content viewed, email open rates, mouse movements,
            keystroke activity, browsing, search, or purchasing behavior).
          </li>
          <li>
            Advertising/advertising measurement data (data associated with your
            view of, or clicks on, advertisements served on our Services, cookie
            IDs or other digital or proprietary IDs).
          </li>
        </ul>
        <p>
          In some cases, the above may not be by itself considered “Personal
          Information” under applicable law and we will use such data as we see
          fit subject to applicable law. However, we may combine
          Analytics/Advertising Data with Personal Information and then shall
          treat such combined information as Personal Information.
        </p>
        <p className="font-semibold text-slate-800 mt-4">
          How We May Collect Analytics/Advertising Data
        </p>
        <p>
          We and our third-party service providers may collect
          Analytics/Advertising Data in a variety of ways, including:
        </p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>
            <strong>Through your browser or device:</strong> MAC address,
            computer type, screen resolution, operating system, language,
            Internet browser type.
          </li>
          <li>
            <strong>Through your use of the Websites or Apps:</strong> We and
            our third-party partners may track and collect App/Website usage
            data.
          </li>
          <li>
            <strong>Using cookies:</strong> Cookies allow us to collect or read
            information such as browser type, time spent on the Services, user
            identifiers, and pages visited.
          </li>
          <li>
            <strong>Using pixel tags and other similar technologies:</strong> To
            track the actions of users, drop cookies, deliver and measure
            marketing campaigns.
          </li>
          <li>
            <strong>Using Adobe Flash technology:</strong> To collect and store
            information about your use of the Services.
          </li>
          <li>
            <strong>IP Address:</strong> Identified and logged automatically in
            our server log files whenever a user accesses the Services.
          </li>
          <li>
            <strong>Physical Location:</strong> By using satellite, cell phone
            tower, or WiFi signals to provide you with personalized
            location-based services.
          </li>
          <li>
            <strong>By aggregating information:</strong> Aggregated Personal
            Information does not personally identify you.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "4. How We May Use Personal Information",
    content: (
      <div className="space-y-4">
        <p>We may use Personal Information:</p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>
            To complete and fulfill your booking or purchase, including to
            process your payment, communicate with you regarding your booking,
            and provide customer service. We use passport information for the
            purpose of travel bookings.
          </li>
          <li>
            To respond to your inquiries and fulfill your requests, such as to
            send you newsletters.
          </li>
          <li>
            To send administrative information to you, such as changes to our
            terms, conditions, and policies.
          </li>
          <li>
            To send you marketing communications that we believe may be of
            interest to you, present products and offers tailored to you.
          </li>
          <li>
            To allow you to participate in sweepstakes, contests, and similar
            promotions.
          </li>
          <li>To facilitate social sharing functionality.</li>
          <li>
            To allow you to send messages to another person through the
            Services.
          </li>
          <li>
            For our business purposes, such as data analysis, error remediation,
            fraud monitoring, developing new products, enhancing Services,
            identifying usage trends, and determining the effectiveness of our
            promotional campaigns.
          </li>
          <li>
            As we believe to be necessary or appropriate under applicable law,
            to comply with legal process, to respond to requests from public
            authorities, to enforce our terms, and to protect our operations,
            rights, privacy, and safety.
          </li>
          <li>
            To aggregate such information such that it is no longer Personal
            Information.
          </li>
          <li>
            As otherwise disclosed to you upon notice or with your consent.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "5. How Personal Information May Be Disclosed",
    content: (
      <div className="space-y-4">
        <p>
          Your Personal Information may be disclosed or transferred to the
          following entities. With the exception of our corporate affiliates and
          service providers, these entities are not controlled by us and
          Personal Information disclosed to them is subject to the applicable
          entity's privacy policy:
        </p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>
            To our corporate affiliates, parent companies, subsidiaries, or
            other companies under common control with us worldwide.
          </li>
          <li>
            To our third-party travel-related suppliers, such as airlines,
            hotels, and car rental companies.
          </li>
          <li>
            To other travel-related companies with whom we partner, such as
            airline consolidators, aggregators, and travel insurance providers.
          </li>
          <li>
            To our business partners, whose products or services may be offered
            on the Services.
          </li>
          <li>
            To our third-party service providers who provide services such as
            website hosting, data analysis, advertising technology services,
            payment processing, order fulfillment, and customer service.
          </li>
          <li>
            To third-party sponsors or operators of surveys, sweepstakes, and
            contests.
          </li>
          <li>
            To identify you to anyone to whom you send messages through the
            Services or message boards.
          </li>
          <li>
            Subject to your social media account settings, to your friends
            associated with your social media account and your social media
            account provider.
          </li>
          <li>
            In the event of a merger, reorganization, dissolution, or the sale
            of all or substantially all of our assets.
          </li>
          <li>
            As we believe to be necessary or appropriate under applicable law,
            to comply with legal processes, or to protect our rights, privacy,
            safety, or property.
          </li>
        </ul>
      </div>
    ),
  },
  {
    title: "6. Third-Party Services & Advertising",
    content: (
      <div className="space-y-4">
        <p className="font-semibold text-slate-800">Third-Party Services</p>
        <p>
          This Privacy Policy does not address, and we are not responsible for,
          the privacy, information, or other practices of any third parties,
          including any third-party supplier or intermediary, any third party
          operating any site or service to which the Services link. The
          inclusion of a link on the Services does not imply endorsement of the
          linked site. In addition, we are not responsible for the information
          collection, use, disclosure, or security policies of other
          organizations, such as Facebook, Apple, Google, or Microsoft.
        </p>
        <p className="font-semibold text-slate-800 mt-4">More On Advertising</p>
        <p>
          We, and third-party advertising companies, use information that we
          receive about you to serve advertisements more relevant to your
          interests, optimize advertising campaigns, as well as provide
          analytics or advertising-related services. The information used for
          these practices is typically collected through tracking technologies,
          such as cookies, web beacons/pixels, SDKs, and APIs, and is used to
          identify you across devices.
        </p>
        <p>
          If you would like more information about your choices in relation to
          interest-based advertising on web and mobile browsers, please visit
          http://www.networkadvertising.org/managing/opt_out.asp and
          http://www.aboutads.info/choices.
        </p>
      </div>
    ),
  },
  {
    title: "7. Security & Retention Period",
    content: (
      <div className="space-y-4">
        <p className="font-semibold text-slate-800">Security</p>
        <p>
          We seek to use reasonable organizational, technical, and
          administrative measures to protect Personal Information within our
          organization. Unfortunately, no data transmission or storage system
          can be guaranteed to be 100% secure. If you have reason to believe
          that your interaction with us is no longer secure, please immediately
          notify us.
        </p>
        <p className="font-semibold text-slate-800 mt-4">Retention Period</p>
        <p>
          We will retain your Personal Information for the period that we deem
          necessary to fulfill the purposes outlined in this Privacy Policy
          unless a longer retention period is required or permitted by law.
        </p>
      </div>
    ),
  },
  {
    title: "8. Your Choices and Rights",
    content: (
      <div className="space-y-4">
        <p className="font-semibold text-slate-800">
          Opt-Out of Marketing Emails
        </p>
        <p>
          You may opt-out from receiving marketing-related emails from us on a
          going-forward basis by following the instructions contained in each
          such email or by contacting us. You may opt-out of receiving push
          notifications from us through your device settings. Please note that
          if you opt-out of receiving marketing-related emails, we may still
          send you important administrative or transactional messages.
        </p>
        <p className="font-semibold text-slate-800 mt-4">
          How you can access, correct, or delete your Personal Information
        </p>
        <p>
          If you would like to access, correct, or delete Personal Information
          that you have previously provided to us, you may submit your request
          by contacting us via email at reservations@TripReserver.com and
          following the applicable steps in the email you receive from us. For
          your protection, we may only implement requests with respect to the
          Personal Information associated with the particular email address that
          you use to send us your request.
        </p>
      </div>
    ),
  },
  {
    title: "9. Specific Resident Privacy Rights",
    content: (
      <div className="space-y-4">
        <p className="font-semibold text-slate-800">
          Important Notice to All Non-US Residents
        </p>
        <p>
          Our servers are located in the U.S. If you are located outside of the
          U.S., please be aware that any information provided to us, including
          personal information, will be transferred from your country of origin.
          Except in the case of data transfers with respect to the GDPR, your
          decision to provide such data to us constitutes your consent to this
          data transfer.
        </p>
        <p className="font-semibold text-slate-800 mt-4">GDPR Notice</p>
        <p>
          Natural persons located in the European Economic Area (“EEA”) should
          review the General Data Protection Regulation 2016/679 (“GDPR”)
          privacy notice regulations applicable to them.
        </p>
        <p className="font-semibold text-slate-800 mt-4">
          Your U.S. State Privacy Law Rights
        </p>
        <ul className="list-inside list-disc space-y-2 pl-2">
          <li>
            <strong>U.S. State Privacy Laws:</strong> If you are a resident of
            California, Colorado, Connecticut, Delaware, Indiana, Iowa,
            Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New
            Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia,
            specific state privacy laws may apply to you.
          </li>
          <li>
            <strong>Consumer Health Laws:</strong> If you are a resident of
            Washington State or Nevada, please review the respective Consumer
            Health Data Privacy Notices.
          </li>
          <li>
            <strong>“Shine the Light” Law:</strong> If you are a resident of
            California, you may request information regarding the disclosure of
            your Personal Information to our affiliates and to third parties for
            the recipients' own direct marketing purposes.
          </li>
          <li>
            <strong>Do Not Track:</strong> Besides Global Privacy Control, we do
            not respond to Do Not Track (DNT) signals or similar signals.
          </li>
        </ul>
        <p className="font-semibold text-slate-800 mt-4">
          Your Canadian Privacy Rights
        </p>
        <p>
          If you are a resident of Canada, you may request information regarding
          the disclosure of your Personal Information to our affiliates and to
          third parties for the recipients' own direct marketing purposes.
        </p>
      </div>
    ),
  },
  {
    title: "10. Minors & Sensitive Information",
    content: (
      <div className="space-y-4">
        <p className="font-semibold text-slate-800">
          Use of The Services By Minors
        </p>
        <p>
          The Services are not directed to individuals under the age of eighteen
          (18), and we request that they not provide Personal Information
          through the Services.
        </p>
        <p className="font-semibold text-slate-800 mt-4">
          Sensitive Information
        </p>
        <p>
          We ask that you not send us, and you not disclose, any sensitive
          Personal Information (e.g., Social Security numbers, information
          related to racial or ethnic origin, political opinions, religion or
          other beliefs, health, biometrics or genetic characteristics, criminal
          background, or trade union membership) on or through the Services or
          otherwise to us.
        </p>
      </div>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <section className="bg-slate-50 py-10 sm:py-14 lg:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-[#2871d3] transition-colors hover:text-[#155bc2]"
        >
          <FaArrowLeft aria-hidden="true" />
          Back to home
        </Link>

        <header className="border-b border-slate-200 pb-8 sm:pb-10">
          <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-[#2871d3] text-white shadow-sm">
            <FaShieldAlt className="text-xl" aria-hidden="true" />
          </div>
          <p className="mb-3 text-sm font-bold uppercase tracking-wide text-[#2871d3]">
            TripReserver
          </p>
          <h1 className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
            Privacy Policy
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base">
            Last updated: September 30, 2026. This policy explains how
            TripReserver handles personal information when you use our website
            or contact us for travel assistance.
          </p>
        </header>

        <article className="py-8 sm:py-10">
          <p className="mb-8 text-base leading-8 text-slate-700">
            By using TripReserver, you acknowledge the practices described in
            this Privacy Policy. We encourage you to read it together with our{" "}
            <Link
              to="/terms-and-conditions"
              className="font-semibold text-[#2871d3] underline underline-offset-4 hover:text-[#155bc2]"
            >
              Terms &amp; Conditions
            </Link>
            .
          </p>

          <div className="space-y-12">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="mb-4 text-xl font-bold text-slate-900 sm:text-2xl">
                  {section.title}
                </h2>
                <div className="text-sm leading-7 text-slate-600 sm:text-base">
                  {section.content}
                </div>
              </section>
            ))}

            <section>
              <h2 className="mb-4 text-xl font-bold text-slate-900 sm:text-2xl">
                11. Updates to This Privacy Policy & Contacting Us
              </h2>
              <div className="space-y-4 text-sm leading-7 text-slate-600 sm:text-base">
                <p>
                  We may change this Privacy Policy (including any constituent
                  part included herein). The “Last updated” date at the top of
                  this page indicates when this Privacy Policy was last revised.
                  Any changes will become effective when we post the revised
                  Privacy Policy on the Websites, Apps, and the Services, as
                  applicable. Your use of the Websites, Apps, or the Services,
                  as applicable, following these changes means that you accept
                  the revised Privacy Policy.
                </p>
                <p>
                  If you have any questions about this Privacy Policy, please
                  contact us via email at{" "}
                  <a
                    href="mailto:reservations@TripReserver.com?subject=Privacy%20Policy%20Question"
                    className="font-semibold text-[#2871d3] underline underline-offset-4 hover:text-[#155bc2]"
                  >
                    reservations@TripReserver.com
                  </a>
                  .
                </p>
                <p className="italic text-slate-500">
                  Because email communications are not always secure, please do
                  not include credit card or other sensitive information in your
                  emails to us.
                </p>
              </div>
            </section>
          </div>
        </article>

        <div className="border-t border-slate-200 pt-7">
          <Link
            to="/terms-and-conditions"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#2871d3] transition-colors hover:text-[#155bc2]"
          >
            <FaEnvelope aria-hidden="true" />
            Read Terms &amp; Conditions
          </Link>
        </div>
      </div>
    </section>
  );
}
